// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. The original lives
// in @roc/feature-borrowers and is left untouched so other portals keep the old
// design. MobX stores/services remain shared (imported from @roc/feature-borrowers).

import { Divider, Grid, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Button, FileUpload, FileUploadArea, FileUploadAreaWithDownload, Tabs, createAutocompleteAddressField, createCheckboxField, createCurrencyField, createDateField, createSelectField, createTextField } from '@roc/ui';
import {
  PropertyInformationFormLineItemV2 as PropertyInformationFormLineItem,
  PublicTrackRecordLineItemV2 as PublicTrackRecordLineItem,
} from '../components/borrowerBaseballCardV2';
import { stateOptions } from 'libs/client-portal-shared/src/app/modules/bridgePricer/utils/bridgePricerConstants';
import { useEffect, useState } from 'react';
import { useDownloadTrackRecordTemplate } from '../hooks/useDownloadTrackRecordTemplate';
import { BorrowerTrackRecordVerificationStore, BorrowerBaseballCardBaseStore } from '@roc/feature-borrowers';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Box } from '@material-ui/core';
import { ChatSection, FilterType, useLoanCommunicationStore } from '@roc/feature-communication';
import { ChatContainer } from '@chatscope/chat-ui-kit-react';
import { ForumOutlined } from '@material-ui/icons';

const TAB_TRACK_RECORD = 'Track Record';
const TAB_CHAT = 'Chat';

const useStyles = makeStyles((theme: Theme) => {
  return {
    button: {
      marginRight: theme.spacing(1)
    },
    // the chat brings its own pale canvas + cards; this box just rounds its corners
    chatContainer: {
      height: '60vh',
      borderRadius: 16,
      overflow: 'hidden',
    },
    createChatButtonContainer: {
      height: '60vh',
      border: '1px solid #CBCBCB',
      borderRadius: '10px',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    createChatButton: {
      letterSpacing: 'normal',
      textTransform: 'none',
      fontWeight: 'normal',
      fontSize: '16px',
      backgroundColor: '#4285F4',
      width: '307px',
      height: '64px',
      padding: '8px 16px',
      boxShadow: '0px 4px 4px 0px #00000040',
      '&:hover': {
        backgroundColor: '#357AE8',
      },
    },
    circleIconContainer: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '5px',
    },
  };
});

export const dealTypeOptions = [
  {
    label: 'Bridge',
    value: 'Bridge',
  },
  {
    label: 'Rental',
    value: 'Rental',
  },
  {
    label: 'Ground Up',
    value: 'Ground Up'
  }
];

const screenTypeTitles = {
  entityName: 'Look Up by Entity Name',
  propertyAddress: 'Look Up by Property Address',
  upload: 'Upload Track Record',
};

interface TrackRecordPropertyFormProps {
  store: BorrowerTrackRecordVerificationStore;
  handleClose: () => void;
  onContinue?: (fileName: string) => void;
  borrowerBaseballCardStore: BorrowerBaseballCardBaseStore;
  onEntityInvokeAgent?: (entityNames: string[]) => void;
  onPropertyInvokeAgent?: (customTransactions: any[]) => void;
  onManualAddInvokeAgent?: (data: any) => void;
}

export const TrackRecordPropertyForm = observer(({ store, handleClose, onContinue, borrowerBaseballCardStore, onEntityInvokeAgent, onPropertyInvokeAgent, onManualAddInvokeAgent }: TrackRecordPropertyFormProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const formStore = store.trackRecordPropertyFormStore;
  const [templateUploads, setTemplateUploads] = useState<FileUpload[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const { loanCommunicationStore } = useLoanCommunicationStore();
  const downloadTrackRecordTemplate = useDownloadTrackRecordTemplate();

  useEffect(() => {
    if (templateUploads.length === 0) return;
    store.setTemplateUploads(templateUploads);
    store.parseTrackRecordTemplate(() => {});
  }, [templateUploads]);

  const handleAddressChange = event => {
    const { value, fullInfo } = event?.target || {};
    const { address_components, geometry } = fullInfo || {};
    formStore.saveAddressField(value, address_components, geometry);
  };

  // Mirrors master AddNewPropertyForm.handleSavePropertyInformation
  const handleSaveManualProperty = async () => {
    if (formStore.form.meta.isValid) {
      // Snapshot the user-entered values BEFORE save: saveTrackRecordProperty resets the form,
      // and the dealType label is derived from the checkboxes only during save.
      const propertyFields = formStore.form.fields;
      const toTrimmedString = (value: any) =>
        (value === null || value === undefined || value === '' ? undefined : String(value));
      // Dates: normalize to yyyy-MM-dd so the backend can parse them (the field can hold a JS Date).
      const toIsoDate = (value: any) => {
        if (value === null || value === undefined || value === '') return undefined;
        const parsedDate = new Date(value);
        return isNaN(parsedDate.getTime()) ? String(value) : parsedDate.toISOString().slice(0, 10);
      };
      const isBridge = formStore.bridgeSelected;
      const isGroundUp = formStore.groundUpSelected;
      const isTerm = formStore.termSelected;
      const dealTypeLabel = isGroundUp ? 'Ground Up' : isBridge ? 'Bridge' : isTerm ? 'Rental' : undefined;
      const manualInvokeRequest = {
        manualProperty: {
          property_address: toTrimmedString(
            (propertyFields.purchaseAddress?.value || propertyFields.fullAddress?.value || '')
              .toString().split(',')[0].trim()
          ),
          city: toTrimmedString(propertyFields.purchaseCity?.value),
          state: toTrimmedString(propertyFields.purchaseState?.value),
          zipcode: toTrimmedString(propertyFields.purchaseZipcode?.value),
          type_of_deal: toTrimmedString(propertyFields.dealType?.value) || dealTypeLabel,
          purchase_price: toTrimmedString(propertyFields.purchasePrice?.value),
          purchase_date: toIsoDate(propertyFields.purchaseDate?.value),
          sale_price: toTrimmedString(propertyFields.salePrice?.value),
          sale_date: toIsoDate(propertyFields.saleDate?.value),
          renovation_budget: toTrimmedString(propertyFields.rehabBudget?.value),
        },
        bridge: isBridge,
        groundUp: isGroundUp,
        term: isTerm,
        recordSourceType: 'User Inputted',
      };
      // Editing an existing record must not re-send it to the agent as a new manual property.
      const isEditing = store.currentProperty?.verifiedTrackRecordsId != null;
      store.setShouldPropagateDealType(false);
      await store.saveTrackRecordProperty();
      borrowerBaseballCardStore.setPropagateAddress(false);
      // Also send the manually-entered property to the agent so it lands in track_records_v2
      if (!isEditing) store.invokeAgent(manualInvokeRequest);
    } else {
      formStore.runFormValidation();
      store.globalStore.notificationStore.showErrorNotification({
        message: 'Please fill the missing fields',
      });
    }
  };

  const getItemListFromBothSets = () => {
    const firstArray = store.currentDocumentsToUpload;
    const secondArray = store.currentTasks;

    const mergedArray = firstArray.map((itemA) => {
      const itemB = secondArray.find((itemB) => itemB.originalFileName === itemA.name);
      return itemB ? { ...itemA, taskId: itemB.taskId, originalFileName: itemB.originalFileName, fileExtension: itemB.fileExtension }
        : itemA;
    })
    return mergedArray;
  }

  useEffect(() => {
    borrowerBaseballCardStore.setCurrentBorrower(store?.borrowerInfo?.borrowerId);
    if (borrowerBaseballCardStore.propagateAddress) {
      if (borrowerBaseballCardStore?.propertyFormStore?.form?.fields?.purchaseAddress?.value !== '') {
        formStore.onFieldChange('fullAddress', borrowerBaseballCardStore?.propertyFormStore?.form?.fields?.purchaseAddress?.value);
        formStore.onFieldChange('purchaseAddress', borrowerBaseballCardStore?.propertyFormStore?.form?.fields?.purchaseAddress?.value);
        formStore.onFieldChange('purchaseState', borrowerBaseballCardStore?.propertyFormStore?.form?.fields?.purchaseState?.value);
        formStore.onFieldChange('purchaseZipcode', borrowerBaseballCardStore?.propertyFormStore?.form?.fields?.purchaseZipcode?.value);
        formStore.onFieldChange('purchaseCity', borrowerBaseballCardStore?.propertyFormStore?.form?.fields?.purchaseCity?.value);
      }
    }
  }, [borrowerBaseballCardStore.propagateAddress])

  return (
    <Grid container direction='row' spacing={2}>
      {formStore.form.fields.screenType.value !== 'upload' && (
        <Grid item xs={12}>
          <Typography variant='h4'>
            {screenTypeTitles[formStore.form.fields.screenType.value] || 'Property Details'}
          </Typography>
        </Grid>
      )}
      {formStore.form.fields.screenType.value === 'propertyAddress' ? (
        <>
          {<PropertyInformationFormLineItem store={borrowerBaseballCardStore}
            title='Property Details' doPropertyLookup={true} onClose={handleClose} formStore={formStore} onSearchInvokeAgent={onPropertyInvokeAgent} onManualAddInvokeAgent={onManualAddInvokeAgent} />}
        </>
      ) : formStore.form.fields.screenType.value === 'entityName' ? (
        <>
          {<PublicTrackRecordLineItem store={borrowerBaseballCardStore} isOpen={store.isPropertyFormActive} handleClose={handleClose} formStore={formStore} onSearchInvokeAgent={onEntityInvokeAgent} />}
        </>
      ) : formStore.form.fields.screenType.value === 'upload' ? (
        <>
          {/* Tabs */}
          <Grid item xs={12} style={{ padding: 0 }}>
            <Tabs
              tabs={[{ label: TAB_TRACK_RECORD }, { label: TAB_CHAT }]}
              selectedTab={selectedTab}
              onTabChange={(e, index) => setSelectedTab(index)}
              variant={''}
            />
          </Grid>

          {/* Borrower header */}
          <Grid item xs={12} style={{ padding: 0 }}>
            <Box style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', padding: '12px 20px' }}>
              <Typography variant='subtitle1' style={{ fontWeight: 700, fontSize: 15 }}>
                {store.borrowerInfo?.firstName || store.borrowerInfo?.borrowerName || 'Unknown'}
                {store.borrowerInfo?.lastName ? ` ${store.borrowerInfo.lastName}` : ''}
                {' - '}
                <span style={{ color: '#888', fontWeight: 400 }}>Track Record</span>
              </Typography>
            </Box>
          </Grid>

          {/* Track Record tab content */}
          {selectedTab === 0 && (
            <>
              {/* Title */}
              <Grid item xs={12}>
                <Typography variant='h5' style={{ fontSize: 18, fontWeight: 600 }}>Upload Track Record</Typography>
              </Grid>

              <Grid item xs={12}>
                <FileUploadArea
                  items={templateUploads}
                  setItems={(items) => setTemplateUploads(items)}
                  multiple={false}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display='flex' justifyContent='space-between' alignItems='center' style={{ padding: '16px 20px', backgroundColor: '#F5F5F5', borderRadius: 4 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', color: '#014361', fontSize: 15 }}>
                    <InfoOutlinedIcon style={{ fontSize: 22, color: '#0288D1', marginRight: 6 }} />
                    Don't have a template?
                  </span>
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', color: theme.palette.primary.main, cursor: 'pointer', fontSize: 14 }}
                    onClick={downloadTrackRecordTemplate}
                  >
                    Download Capital Provider template
                  </span>
                </Box>
              </Grid>

              {/* Footer */}
              <Grid item xs={12}>
                <Divider style={{ margin: '16px 0' }} />
                <Box display='flex' justifyContent='space-between' alignItems='center' style={{ paddingBottom: 24 }}>
                  <Typography variant='body2' color='textSecondary'>
                    Providing details now helps us verify faster. You can also skip and add experience later from your<br />dashboard
                  </Typography>
                  <Box display='flex' style={{ gap: 8 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onContinue ? onContinue(templateUploads[0]?.name || '') : handleClose()}
                      testId='continueUpload'
                      className={classes.button}
                    >
                      Continue
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </>
          )}

          {/* Chat tab content */}
          {selectedTab === 1 && (
            <Grid item xs={12} style={{ padding: 16 }}>
              {loanCommunicationStore.currentConversation ? (
                <Box className={classes.chatContainer}>
                  <ChatSection
                    as={ChatContainer}
                    communicationStore={loanCommunicationStore}
                    allowJoinConversation={true}
                    allowLeaveConversation={true}
                    showSnippet={false}
                    showEmailAddress={false}
                    allowManageParticipants={true}
                    allowAddInternalParticipants={true}
                    allowAddParticipants={true}
                    allowRemoveParticipants={true}
                    onDownloadConversation={undefined}
                    onUnarchiveConversation={() => {}}
                    showLoading={false}
                  />
                </Box>
              ) : (
                <Box className={classes.createChatButtonContainer}>
                  <Button
                    testId="startChat"
                    onClick={() => {
                      loanCommunicationStore.loadMyConversations(1, 100, () => {}, () => {});
                    }}
                    variant="contained"
                    className={classes.createChatButton}
                  >
                    <Box display="flex" alignItems="center">
                      <Box className={classes.circleIconContainer}>
                        <ForumOutlined style={{ color: '#4285F4' }} />
                      </Box>
                      <Typography variant="body1" style={{ fontSize: '16px', fontWeight: 'bold', marginRight: '8px', color: '#FFFFFF' }}>
                        Start Conversation
                      </Typography>
                    </Box>
                  </Button>
                  <Typography variant="body1" style={{ marginTop: '5px', fontSize: '12px', fontWeight: 'normal' }}>
                    Contact us with any questions about your track record.
                  </Typography>
                </Box>
              )}
            </Grid>
          )}
        </>
      ) : <>
        <Grid item xs={12} container direction='row' spacing={1}>
            <Grid item xs={6}>
              {createAutocompleteAddressField({
                fieldName: 'fullAddress',
                label: 'Address Line 1',
                testId: 'fullAddress',
                store: formStore,
                onChange: handleAddressChange,
              })}
            </Grid>
            <Grid item xs={6}>
              {createTextField({
                label: 'Address Line 2',
                fieldName: 'address2',
                testId: 'address2',
                store: formStore,
              })}
            </Grid>
            <Grid item xs={4}>
              {createTextField({
                label: 'City',
                fieldName: 'purchaseCity',
                testId: 'purchaseCity',
                store: formStore,
              })}
            </Grid>
            <Grid item xs={4}>
              {createSelectField({
                store: formStore,
                testId: 'purchaseState',
                fieldName: 'purchaseState',
                label: 'State',
                options: stateOptions,
              })}
            </Grid>
            <Grid item xs={4}>
              {createTextField({
                label: 'Zip Code',
                fieldName: 'purchaseZipcode',
                testId: 'purchaseZipcode',
                store: formStore,
              })}
            </Grid>
            <Grid item xs={6}>
              {createTextField({
                store: formStore,
                testId: 'entityName',
                fieldName: 'entityName',
                label: 'Entity on Title',
              })}
              </Grid>
            <Grid item xs={12} style={{ marginTop: '10px', marginBottom: '20px' }}>
              <Typography variant="body1" gutterBottom>
                Deal Type
              </Typography>
              <Grid container spacing={2} direction="row" alignItems="flex-start">
                <Grid item>
                  {createCheckboxField({
                    label: 'Fix And Flip',
                    fieldName: 'bridge',
                    store: formStore,
                    disabled: formStore.groundUpSelected,
                  })}
                </Grid>
                <Grid item>
                  {createCheckboxField({
                    label: 'Rental',
                    fieldName: 'term',
                    store: formStore,
                    disabled: (formStore.groundUpSelected || formStore.bridgeSelected),
                  })}
                </Grid>
                <Grid item>
                  {createCheckboxField({
                    label: 'Ground Up',
                    fieldName: 'groundUp',
                    store: formStore,
                    disabled: (formStore.bridgeSelected && !formStore.groundUpSelected),
                  })}
                </Grid>
              </Grid>
            </Grid>
            {((formStore.groundUpSelected || formStore.bridgeSelected) || (!formStore.termSelected && !formStore.groundUpSelected && !formStore.bridgeSelected)) && (
              <Grid item xs={6}>
                {createCurrencyField({
                  label: 'Renovation Budget',
                  fieldName: 'rehabBudget',
                  testId: 'rehabBudget',
                  store: formStore,
                })}
              </Grid>
            )}
              <Grid item xs={6}>
                {createCurrencyField({
                  store: formStore,
                  label: 'Purchase Amount',
                  fieldName: 'purchasePrice',
                  testId: 'purchasePrice',
                })}
              </Grid>
            {formStore.form.fields.dealType.value != 'Rental' ?
                (<Grid item xs={6}>
                {createDateField({
                  label: 'Purchase Date',
                  fieldName: 'purchaseDate',
                  testId: 'purchaseDate',
                  format: 'MM/dd/yyyy',
                  store: formStore,
                })}
              </Grid>) :
              (<Grid item xs={6}>
                {createDateField({
                  label: 'Purchase Date',
                  fieldName: 'purchaseDate',
                  testId: 'purchaseDate',
                  format: 'MM/dd/yyyy',
                  store: formStore,
                })}
                </Grid>)}
              <Grid item xs={6}>
                {createTextField({
                  store: formStore,
                  label: 'Seller Name',
                  fieldName: 'purchasedFrom',
                  testId: 'purchasedFrom',
                })}
              </Grid>
              <Grid item xs={6}>
                {createDateField({
                  store: formStore,
                  label: 'Sale Date',
                  fieldName: 'saleDate',
                  testId: 'saleDate',
                })}
              </Grid>
              <Grid item xs={6}>
                {createCurrencyField({
                  store: formStore,
                  label: 'Sale Amount',
                  fieldName: 'salePrice',
                  testId: 'salePrice',
                })}
              </Grid>
          </Grid>
          <Grid item xs={12}>
              <span>{`Supporting Documents`}</span>
            </Grid>
            <Grid item xs={12}>
            <FileUploadAreaWithDownload
              items={
                getItemListFromBothSets()
              }
              setItems={(updateFunc) => {
                const updatedItems = updateFunc(store.currentDocumentsToUpload);
                store.currentDocumentsToUpload = updatedItems;
              }}
              multiple={true}
              store={store}
            />
          </Grid>
          {/* Comments — shown when editing an existing record (matches master,
              which gates on dialog state != ADD). */}
          {store.currentProperty?.verifiedTrackRecordsId != null && (
            <Grid item xs={12} style={{ paddingBottom: '10px' }}>
              {createTextField({
                label: 'Comments',
                fieldName: 'comment',
                testId: 'comment',
                store: formStore,
                multiline: true,
                minRows: 2,
              })}
            </Grid>
          )}
          <Grid container justifyContent='flex-end'>
            <Button
              variant='outlined'
              color='primary'
                onClick={() => { handleClose(); borrowerBaseballCardStore.setPropagateAddress(false); }}
              testId='removeProperty'
              className={classes.button}
              style={{ backgroundColor: 'transparent' }}
            >
              Cancel
              </Button>
            <Button
              variant="contained"
              color="primary"
                onClick={() => { handleSaveManualProperty(); }}
              testId='saveProperty'
              className={classes.button}
            >
              Save
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Divider variant="fullWidth" />
          </Grid>
      </>}

    </Grid>
  );
});
